import { useNavigate } from '@tanstack/react-router';
import { Inbox } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useInboxChannels,
  useInboxTags,
  useInboxConversation,
  useInboxConversations,
  useInboxNotices,
  useMarkInboxRead,
  useMarkInboxUnread,
  useUpdateInboxStatus,
} from '@/lib/api/inbox';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';

import { ConversationDetails } from './conversation-details';
import { ConversationList } from './conversation-list';
import { ConversationThread } from './conversation-thread';
import { readInboxChannels, saveInboxChannels } from './inbox-channel-preference';
import { inboxKeyAction, isTypingTarget, neighbourId } from './inbox-keys';
import { InboxNotices } from './inbox-notices';
import { type InboxSearch, inboxQueryFor, withSearch } from './inbox-search';

export function InboxWorkspace({
  search: urlSearch,
  conversationId,
}: {
  search: InboxSearch;
  conversationId: string | undefined;
}) {
  const navigate = useNavigate();
  const viewerId = useAuth().user?.id;
  const [restoring, setRestoring] = useState(() =>
    viewerId && !urlSearch.channel ? readInboxChannels(viewerId) : undefined,
  );
  const search = useMemo(
    () => (restoring ? withSearch(urlSearch, { channel: restoring }) : urlSearch),
    [restoring, urlSearch],
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const conversations = useInboxConversations(inboxQueryFor(search));
  const channels = useInboxChannels();
  const tags = useInboxTags();
  const notices = useInboxNotices();
  const { mutate: setStatus } = useUpdateInboxStatus();
  const { mutate: markUnread } = useMarkInboxUnread();
  const ids = conversations.data?.items.map((item) => item.id) ?? [];

  const goTo = useCallback(
    (next: InboxSearch, replace?: boolean) =>
      void navigate(
        conversationId
          ? { to: '/inbox/$conversationId', params: { conversationId }, search: next, replace }
          : { to: '/inbox', search: next, replace },
      ),
    [navigate, conversationId],
  );

  const updateSearch = (patch: Partial<InboxSearch>, options: { replace?: boolean } = {}) => {
    const next = withSearch(search, patch);
    if ('channel' in patch && viewerId) {
      saveInboxChannels(viewerId, next.channel ?? []);
    }
    goTo(next, options.replace);
  };

  const restoredSearch = restoring ? search : undefined;
  useEffect(() => {
    if (!restoredSearch) {
      return;
    }
    if (urlSearch.channel) {
      setRestoring(undefined);
      return;
    }
    goTo(restoredSearch, true);
  }, [restoredSearch, urlSearch.channel, goTo]);

  const open = useCallback(
    (id: string) => {
      setDetailsOpen(false);
      void navigate({ to: '/inbox/$conversationId', params: { conversationId: id }, search });
    },
    [navigate, search],
  );

  const back = () => {
    setDetailsOpen(false);
    void navigate({ to: '/inbox', search });
  };

  const leaveUnread = useCallback(
    (id: string) => {
      setDetailsOpen(false);
      void navigate({ to: '/inbox', search });
      markUnread(id);
    },
    [navigate, search, markUnread],
  );

  const idsKey = ids.join(',');
  useEffect(() => {
    const visible = idsKey ? idsKey.split(',') : [];
    const onKeyDown = (event: KeyboardEvent) => {
      const action = inboxKeyAction({
        key: event.key,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        typing: isTypingTarget(event.target),
      });
      if (!action) {
        return;
      }
      event.preventDefault();
      if (action === 'search') {
        searchInputRef.current?.focus();
        return;
      }
      if (action === 'done') {
        if (!conversationId) {
          return;
        }
        const following =
          neighbourId(visible, conversationId, 1) ?? neighbourId(visible, conversationId, -1);
        setStatus({ id: conversationId, status: 'dismissed' });
        if (following) {
          open(following);
        }
        return;
      }
      if (action === 'unread') {
        if (conversationId) {
          leaveUnread(conversationId);
        }
        return;
      }
      const target = neighbourId(visible, conversationId, action === 'next' ? 1 : -1);
      if (target) {
        open(target);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [idsKey, conversationId, open, setStatus, leaveUnread]);

  useEffect(() => {
    if (!conversationId) {
      return;
    }
    document
      .querySelector(`[data-conversation-id="${conversationId}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [conversationId]);

  return (
    <div className="-m-4 flex h-[calc(100dvh-3.5rem)] min-w-0 flex-col overflow-hidden [contain:inline-size] md:-m-6">
      <InboxNotices notices={notices.data?.notices ?? []} />
      <div className="flex min-h-0 min-w-0 flex-1">
        <aside
          className={cn(
            'min-h-0 w-full shrink-0 flex-col border-r md:flex md:w-80 lg:w-96',
            conversationId ? 'hidden' : 'flex',
          )}
          aria-label="Conversation list"
        >
          <ConversationList
            search={search}
            page={conversations.data}
            channels={channels.data?.channels ?? []}
            tags={tags.data?.tags ?? []}
            loading={conversations.isPending}
            error={conversations.error}
            selectedId={conversationId}
            searchInputRef={searchInputRef}
            onSearchChange={updateSearch}
            onSelect={open}
          />
        </aside>
        <main
          className={cn(
            'min-h-0 min-w-0 flex-1 flex-col md:flex',
            conversationId ? 'flex' : 'hidden',
          )}
        >
          {conversationId ? (
            <OpenConversation
              key={conversationId}
              conversationId={conversationId}
              detailsOpen={detailsOpen}
              onBack={back}
              onMarkUnread={() => leaveUnread(conversationId)}
              onDetailsOpenChange={setDetailsOpen}
            />
          ) : (
            <EmptyThread />
          )}
        </main>
      </div>
    </div>
  );
}

function OpenConversation({
  conversationId,
  detailsOpen,
  onBack,
  onMarkUnread,
  onDetailsOpenChange,
}: {
  conversationId: string;
  detailsOpen: boolean;
  onBack: () => void;
  onMarkUnread: () => void;
  onDetailsOpenChange: (open: boolean) => void;
}) {
  const detail = useInboxConversation(conversationId);
  const markRead = useMarkInboxRead();
  const conversation = detail.data;
  const unread = conversation?.unread === true;
  const lastInboundAt = conversation?.lastInboundAt;
  const { mutate: mark } = markRead;

  useEffect(() => {
    if (unread) {
      mark(conversationId);
    }
  }, [conversationId, unread, lastInboundAt, mark]);

  if (detail.isPending) {
    return <p className="p-6 text-sm text-[var(--color-muted-foreground)]">Loading…</p>;
  }
  if (detail.isError || !conversation) {
    return (
      <p className="p-6 text-sm text-[var(--color-destructive)]">
        Failed to load the conversation: {detail.error?.message}
      </p>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="min-h-0 min-w-0 flex-1">
        <ConversationThread
          conversation={conversation}
          onBack={onBack}
          onMarkUnread={onMarkUnread}
          onOpenDetails={() => onDetailsOpenChange(true)}
        />
      </div>
      <aside
        className="hidden min-h-0 w-80 shrink-0 border-l xl:block"
        aria-label="Conversation details"
      >
        <ConversationDetails conversation={conversation} />
      </aside>
      {detailsOpen && (
        <div className="fixed inset-0 z-40 xl:hidden">
          <button
            type="button"
            aria-label="Close details"
            className="absolute inset-0 bg-black/40"
            onClick={() => onDetailsOpenChange(false)}
          />
          <div className="absolute inset-y-0 right-0 w-full max-w-sm border-l bg-[var(--color-background)] shadow-xl">
            <ConversationDetails
              conversation={conversation}
              onClose={() => onDetailsOpenChange(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyThread() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-[var(--color-muted-foreground)]">
      <Inbox className="size-8" />
      <p className="text-sm">Pick a conversation to read and reply.</p>
      <p className="text-xs">J / K to move · E to mark done · U to mark unread · / to search</p>
    </div>
  );
}
